/**
 * Suggestions Admin — super_admin surface for triaging every
 * improvement suggestion submitted across the platform.
 *
 * Route: /workspace/suggestions/admin
 * Auth : super_admin only (backend enforces + we hard-block here).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, ClipboardText } from "@phosphor-icons/react";

import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";


const STATUS_OPTS = [
    { value: "all", label: "All statuses" },
    { value: "open", label: "Received" },
    { value: "under_review", label: "Under review" },
    { value: "converted", label: "Converted" },
    { value: "not_planned", label: "Not planned" },
];


function formatWhen(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
        });
    } catch { return iso; }
}


function StatusBadge({ status }) {
    const map = {
        open: "bg-slate-100 text-slate-700",
        under_review: "bg-amber-100 text-amber-800",
        converted: "bg-emerald-100 text-emerald-800",
        not_planned: "bg-rose-100 text-rose-800",
    };
    const labels = {
        open: "Received",
        under_review: "Under review",
        converted: "Converted",
        not_planned: "Not planned",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-slate-100 text-slate-700"}`}>
            {labels[status] || status}
        </span>
    );
}


function ReviewDialog({ suggestion, open, onOpenChange, onDone }) {
    const [status, setStatus] = useState(suggestion?.status || "under_review");
    const [note, setNote] = useState(suggestion?.reviewer_note || "");
    const [wfi, setWfi] = useState(suggestion?.converted_to_workflow_instance_id || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setStatus(suggestion?.status || "under_review");
        setNote(suggestion?.reviewer_note || "");
        setWfi(suggestion?.converted_to_workflow_instance_id || "");
    }, [suggestion]);

    if (!suggestion) return null;

    const save = async () => {
        setBusy(true);
        try {
            if (status === "converted" && suggestion.status !== "converted") {
                await api.post(`/suggestions/${suggestion.id}/convert`, {
                    workflow_instance_id: wfi.trim() || null,
                    reviewer_note: note.trim() || null,
                });
            } else {
                await api.patch(`/suggestions/${suggestion.id}`, {
                    status,
                    reviewer_note: note.trim() || null,
                });
            }
            toast.success("Suggestion updated");
            onOpenChange(false);
            onDone?.();
        } catch (err) {
            toast.error(formatApiError(err) || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
            <DialogContent
                className="sm:max-w-xl"
                data-testid="sgn-review-dialog"
            >
                <DialogHeader>
                    <DialogTitle>{suggestion.title}</DialogTitle>
                    <DialogDescription>
                        {suggestion.id} · from {suggestion.submitter_name} ({suggestion.submitter_role})
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md border bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap">
                        {suggestion.description}
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                        <div>Category · <strong className="text-slate-700 capitalize">{suggestion.category}</strong></div>
                        <div>Submitted · {formatWhen(suggestion.submitted_at)}</div>
                    </div>

                    <div className="space-y-1.5">
                        <Label>Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="sgn-review-status-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="open">Received</SelectItem>
                                <SelectItem value="under_review">Under review</SelectItem>
                                <SelectItem value="converted">Converted to Project</SelectItem>
                                <SelectItem value="not_planned">Not planned</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {status === "converted" && (
                        <div className="space-y-1.5">
                            <Label htmlFor="wfi">Linked Project Request (optional)</Label>
                            <Input
                                id="wfi"
                                value={wfi}
                                onChange={(e) => setWfi(e.target.value)}
                                placeholder="wfi_… (paste the workflow instance id after you initiate the project)"
                                data-testid="sgn-review-wfi"
                            />
                            <p className="text-xs text-muted-foreground">
                                Initiate the corresponding Project separately from
                                the Projects surface, then paste its
                                <code className="ml-1">wfi_…</code> here for
                                traceability.
                            </p>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label htmlFor="note">Reviewer note (visible to submitter)</Label>
                        <Textarea
                            id="note"
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Optional — what you decided and why."
                            data-testid="sgn-review-note"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="sgn-review-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy}
                        data-testid="sgn-review-save"
                    >
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


export default function SuggestionsAdmin() {
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [reviewing, setReviewing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter !== "all") params.set("status", statusFilter);
            params.set("limit", "200");
            const r = await api.get(`/suggestions?${params}`);
            setItems(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load suggestions");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        if (isSuperAdmin) load();
    }, [load, isSuperAdmin]);

    const counts = useMemo(() => {
        const c = { open: 0, under_review: 0, converted: 0, not_planned: 0 };
        items.forEach((it) => { if (c[it.status] !== undefined) c[it.status]++; });
        return c;
    }, [items]);

    if (!isSuperAdmin) {
        return (
            <EmptyState
                title="Super admin only"
                description="The Improvement Suggestions triage surface is only visible to super admins."
                data-testid="sgn-admin-forbidden"
            />
        );
    }

    return (
        <div className="space-y-6" data-testid="sgn-admin-page">
            <PageHeader
                eyebrow="MY WORKSPACE · ADMIN"
                title="Improvement Suggestions"
                description="Bugs, ideas and feature requests submitted by every user across the platform.  Triage, respond, and convert to a Project when the idea deserves real work."
                actions={
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        disabled={loading}
                        data-testid="sgn-admin-refresh"
                    >
                        <ArrowClockwise className="mr-1.5 h-4 w-4" />
                        Refresh
                    </Button>
                }
            />

            <div className="flex flex-wrap items-center gap-3">
                <div className="text-sm text-muted-foreground">
                    Received {counts.open} · Under review {counts.under_review} · Converted {counts.converted} · Not planned {counts.not_planned}
                </div>
                <div className="ml-auto w-52">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="sgn-admin-status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="rounded-lg border bg-white p-8 text-center text-sm text-muted-foreground">
                    Loading suggestions…
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    title="No suggestions to review"
                    description="Nothing here yet — when users submit ideas or bugs they will appear here for triage."
                    data-testid="sgn-admin-empty"
                />
            ) : (
                <div className="overflow-hidden rounded-lg border bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                                <th className="px-4 py-2">Title</th>
                                <th className="px-4 py-2">Category</th>
                                <th className="px-4 py-2">Submitter</th>
                                <th className="px-4 py-2">Status</th>
                                <th className="px-4 py-2">Submitted</th>
                                <th className="px-4 py-2 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {items.map((it) => (
                                <tr key={it.id} data-testid={`sgn-admin-row-${it.id}`}>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-medium">{it.title}</div>
                                        <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                                            {it.description}
                                        </div>
                                        {it.converted_to_workflow_instance_id && (
                                            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                                                <ClipboardText className="h-3.5 w-3.5" />
                                                <span className="font-mono">
                                                    {it.converted_to_workflow_instance_id}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs capitalize">{it.category}</td>
                                    <td className="px-4 py-3 align-top text-xs">
                                        <div className="font-medium text-slate-700">{it.submitter_name}</div>
                                        <div className="text-muted-foreground">{it.submitter_role}</div>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <StatusBadge status={it.status} />
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                                        {formatWhen(it.submitted_at)}
                                    </td>
                                    <td className="px-4 py-3 align-top text-right">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setReviewing(it)}
                                            data-testid={`sgn-admin-review-btn-${it.id}`}
                                        >
                                            Review
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <ReviewDialog
                suggestion={reviewing}
                open={!!reviewing}
                onOpenChange={(v) => !v && setReviewing(null)}
                onDone={load}
            />
        </div>
    );
}
